import { useState, useRef, ComponentType } from 'react';
import {
  Box,
  IconButton,
  Text,
  VStack,
  Spinner,
  Icon,
  IconProps,
  Button,
} from '@chakra-ui/react';
import { BsMic, BsMicMute } from 'react-icons/bs';
import { transcribeAudio } from '../hooks/ai_insights';

interface AudioRecorderProps {
  isCatalogue?: boolean;
  isNotes?: boolean;
  onSaveNote?: (note: string) => void;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onSaveNote,
  isCatalogue,
  isNotes,
}) => {
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_RECORDING_MS = 0.5 * 60 * 1000; // 0.5 minutes

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      audioChunks.current = [];

      recorder.ondataavailable = (e) => {
        audioChunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        uploadAudio(audioBlob);
        audioChunks.current = [];
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setTranscribedText(null);

      // Auto stop after 5 minutes
      timerRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_RECORDING_MS);
    } catch (err) {
      console.error('🎙️ Error accessing microphone:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  };

  const uploadAudio = async (blob: Blob) => {
    setProcessing(true);

    const audioFile = new File([blob], 'note.webm', { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', audioFile);

    try {
      const response = await transcribeAudio(formData);

      if (response.success) {
        setTranscribedText(
          response.data.data?.text || 'No transcription returned.',
        );
      } else {
        setTranscribedText('Transcription failed: ' + response.data);
      }
    } catch (error) {
      console.error('Error uploading or transcribing audio:', error);
      setTranscribedText('Failed to transcribe audio.');
    } finally {
      setProcessing(false);
    }
  };

  // const uploadAudio = async (blob: Blob) => {
  //   setProcessing(true);
  //   const formData = new FormData();
  //   formData.append("file", blob, "note.webm");

  //   try {
  //     // const response = await fetch("/api/speech-to-text/transcribe/", {
  //     //   method: "POST",
  //     //   body: formData,
  //     // });
  //     const response = await transcribeAudio(formData);
  //     // const result = await response.json();
  //     setTranscribedText(response?.data || "No transcription available.");
  //   } catch (error) {
  //     console.error("Error uploading or transcribing audio:", error);
  //     setTranscribedText("Failed to transcribe audio.");
  //   } finally {
  //     setProcessing(false);
  //   }
  // };

  return (
    <Box>
      <VStack spacing={4}>
        {/* <Text fontWeight="bold">Voice Note Recorder</Text> */}

        <IconButton
          aria-label={recording ? 'Stop recording' : 'Start recording'}
          // icon={recording ? <BsMicMute  size={24} /> : <BsMic size={24} />}
          icon={
            <Icon
              as={
                recording
                  ? (BsMicMute as ComponentType<IconProps>)
                  : (BsMic as ComponentType<IconProps>)
              }
              boxSize={6}
            />
          }
          colorScheme={recording ? 'red' : 'green'}
          bgColor={recording ? 'red' : '#009800ff'}
          onClick={recording ? stopRecording : startRecording}
          size="lg"
          borderRadius="full"
          isRound
        />

        {audioURL && (
          <Box w="full">
            <Text fontSize="sm">Playback:</Text>
            <audio controls src={audioURL} />
          </Box>
        )}

        {processing && (
          <Box display="flex" alignItems="center" gap={2}>
            <Spinner size="sm" />
            <Text fontSize="sm">Processing transcription...</Text>
          </Box>
        )}

        {transcribedText && (
          <Box w="full" p={4} bg="gray.50" borderRadius="md" boxShadow="sm">
            <Text fontWeight="medium" mb={2}>
              Transcribed Text:
            </Text>
            <Text fontSize="sm" whiteSpace="pre-wrap" mb={4}>
              {transcribedText}
            </Text>
            <Button
              colorScheme="green"
              bg="#006400"
              size="sm"
              alignSelf="flex-end"
              onClick={() => onSaveNote && onSaveNote(transcribedText)}
            >
              {isNotes && 'Save Note'}
              {isCatalogue && 'Process Product Details and Save'}
            </Button>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default AudioRecorder;
